import React, { Fragment } from "react";
import { createShortLink } from "actions/shortLink";
import { fetchMerchantAccountTaxRates } from "actions/taxRate";
import { fetchMerchantAccountShippingRates } from "actions/shippingRate";
import {
  fetchMerchantAccountProductsPrices,
  fetchMerchantAccountCouponsPromotionCodes
} from "actions/merchantAccount";
import { fetchAppCheckout } from "actions/app";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Formik, useFormikContext } from "formik";
import Padder from "components/Padder";
import ProductsFields from "components/ShortLinkForm/Fields/Products";
import CouponsFields from "components/ShortLinkForm/Fields/Coupons";
import ShippingFields from "components/ShortLinkForm/Fields/Shipping";
import TaxRatesFields from "components/ShortLinkForm/Fields/TaxRates";
import PaymentFields from "components/ShortLinkForm/Fields/Payment";
import TrialFields from "components/ShortLinkForm/Fields/Trial";
// import ReceiverFields from "components/ShortLinkForm/Fields/Receiver";
// import RedirectsFields from "components/ShortLinkForm/Fields/Redirects";
import { getShortLinkPreviewTitle } from "utils/shortLink";
import { shortLinkCheckoutPreview } from "components/FormerEditor/common/preview";
import {
  ensureAppCheckout,
  ensureProductsPricesCouponsPromotionCodes,
  ensureTaxRates,
  ensureAttachments,
  ensureShippingRates
} from "utils/hooks";
import { fetchAttachments } from "actions/attachment";
import MenuNavHeader from "components/MenuNavHeader";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import CheckoutDetails from "components/SaleForm/Fields/CheckoutDetails";
import {
  prepareCombinationResultInitialValues,
  prepareCombinationSelectionLabels,
  validateCreateCombinationResult
} from "utils/combination";
import { STATE_KEYS } from "utils/constants/state";
import { composeCheckoutConfigsToShortLink } from "utils/checkout/merge";
import ValidationsBar from "components/ValidationsBar";
import { VALIDATION_CHECKOUT_KEY } from "constants/validation";
import { TAX_ID_COLLECTION_TYPE_STANDARD_OPTION } from "utils/constants/taxRate";
import PhaseList from "components/PhaseList";

const CombinationCheckoutBuilder = ({ actions, goBack, name, activeIndex }) => {
  ensureAppCheckout({
    fetchAppCheckout: actions.fetchAppCheckout
  });

  const {
    values: parentValues,
    setValues: setParentValues
  } = useFormikContext();

  const combinationPath = `${name}.${STATE_KEYS.FORM.CONFIG_LOGIC_CHECKOUTS_COMBOS}[${activeIndex}]`;
  const fieldGroupsPath = `${name}.${STATE_KEYS.FORM.FIELD_GROUPS}`;
  const combination = get(parentValues, combinationPath);
  const resultPath = `${combinationPath}.result`;
  const currentComboResult = get(parentValues, resultPath);

  const checkoutCommonPath = `${name}.${STATE_KEYS.FORM.CONFIG_CHECKOUT_COMMON}`;
  const checkoutCommon = get(parentValues, checkoutCommonPath);

  const checkoutConstantPath = `${name}.${STATE_KEYS.FORM.CONFIG_LOGIC_CHECKOUTS_CONSTANT}`;
  const checkoutConstant = get(parentValues, checkoutConstantPath);

  const fieldGroups = get(parentValues, fieldGroupsPath) || [];
  const selectionLabels = prepareCombinationSelectionLabels({
    combination,
    fieldGroups
  });

  const {
    products,
    prices,
    coupons
  } = ensureProductsPricesCouponsPromotionCodes({
    fetchMerchantAccountProductsPrices:
      actions.fetchMerchantAccountProductsPrices,
    fetchMerchantAccountCouponsPromotionCodes:
      actions.fetchMerchantAccountCouponsPromotionCodes
  });
  const { taxRates } = ensureTaxRates({
    fetchMerchantAccountTaxRates: actions.fetchMerchantAccountTaxRates
  });
  const { shippingRates } = ensureShippingRates({
    fetchMerchantAccountShippingRates: actions.fetchMerchantAccountShippingRates
  });
  ensureAttachments({
    fetchAttachments: actions.fetchAttachments
  });

  const submitForm = (formValues, formikActions) => {
    goBack(formValues.data.product);
    formikActions.setSubmitting(false);
  };

  /**
   * Initial values
   * - just the configuration for the combination
   * - render inherited values when attributes are not set
   * - i.e. toggle is off then show checkout summary for those attributes
   * - VIP: only save the values for the form
   */
  const initialValues = prepareCombinationResultInitialValues(
    currentComboResult
  );
  const inheritedShortLink = composeCheckoutConfigsToShortLink({
    commonConfig: checkoutCommon,
    constantCheckout: checkoutConstant
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitForm}
      validate={validateCreateCombinationResult({
        prices,
        products,
        prices,
        coupons,
        taxRates,
        shippingRates,
        /**
         * Validate as a whole
         */
        constant: checkoutConstant,
        common: checkoutCommon
      })}
    >
      {({ values, submitForm, isSubmitting, errors }) => {
        const commonResources = {
          products,
          prices,
          coupons,
          taxRates,
          shippingRates
        };

        /**
         * Extend form combination selections with constant values so that
         * - preview shows all constant inherited values but
         * - the form is only the combination result values
         */
        const compositeShortLink = composeCheckoutConfigsToShortLink({
          selectedCheckout: values.data.product,
          commonConfig: checkoutCommon,
          constantCheckout: checkoutConstant
        });
        const previewInput = {
          shortLink: compositeShortLink,
          ...commonResources
        };

        const checkoutPreview = shortLinkCheckoutPreview(previewInput);
        const previewSubtotal = getShortLinkPreviewTitle(previewInput);

        const hasValidationMessages = errors && errors.messages;
        const validationNode = hasValidationMessages ? (
          <ValidationsBar
            validations={errors.messages}
            noun="checkout"
            context={VALIDATION_CHECKOUT_KEY.COMBINATIONS}
            values={parentValues}
            setValues={setParentValues}
          />
        ) : null;

        const commonFieldProps = {
          name: STATE_KEYS.SHORT_LINK.CHECKOUT,
          inherited: inheritedShortLink
        };

        return (
          <div className="w-100">
            <MenuNavHeader
              label={previewSubtotal}
              customClasses={{
                container:
                  "flex flex-column justify-between items-end sticky-l bb hairline-1 bg-white bh--inset top-0 z-5",
                content: "pb3 ph3 pl1 pt3 w-100 flex flex-row",
                label: "fw6 f3 word-break"
              }}
              action={{
                onClick: submitForm,
                copy: `Save`,
                disabled: !isEmpty(errors),
                loading: isSubmitting
              }}
              footer={validationNode}
            />
            <div className="ph3">
              <div className="pt3 pb2">
                <div className="flex flex-row justify-between items-center pb2">
                  <div className="fw6 f4 w-100 pointer">When</div>
                </div>
                <div className="pv2">
                  <PhaseList phases={selectionLabels} clamp />
                </div>
                <div className="fw6 f4 w-100 pointer pt3 pb2">Then</div>
              </div>
              {[
                <ProductsFields {...commonFieldProps} />,
                <PaymentFields {...commonFieldProps} />,
                <TrialFields {...commonFieldProps} />,
                <TaxRatesFields
                  {...commonFieldProps}
                  collectionTypeOptions={[
                    TAX_ID_COLLECTION_TYPE_STANDARD_OPTION
                  ]}
                />,
                <CouponsFields {...commonFieldProps} />,
                // <ReceiverFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />,
                <ShippingFields {...commonFieldProps} />,
                // <RedirectsFields />,
                <CheckoutDetails
                  header="Checkout preview"
                  collapseImages={checkoutPreview.collapseImages}
                  {...checkoutPreview.checkout}
                />
              ].map((node, nodeIx) => (
                <div className={nodeIx ? "pt3 pt4-ns" : ""} key={nodeIx}>
                  {node}
                </div>
              ))}
            </div>
            <Padder theme="visible" />
          </div>
        );
      }}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createShortLink,
      fetchAppCheckout,
      fetchMerchantAccountProductsPrices,
      fetchMerchantAccountCouponsPromotionCodes,
      fetchMerchantAccountTaxRates,
      fetchMerchantAccountShippingRates,
      fetchAttachments
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(CombinationCheckoutBuilder);
